/**
 * The check pool's shared contract: a repo's check plan, one job's spec, state and result,
 * the result cache key, and the payloads of the worker (`checkPool.*`) and host
 * (`v3.host.checks.*`) rpcs. The rpc groups themselves live in `@fm/api`
 * (`rpc/v3/check-pool.rpc.ts`, `rpc/v3/v3-host-checks.rpc.ts`), because they carry the api's
 * auth middleware.
 *
 * **How a check runs on the pool.** The loop host asks the repo for its plan (the
 * `checkPlan` command of its `orca.json`, which prints a {@link CheckPlan}), uploads the
 * commit's source to a presigned url (`v3.host.checks.sourceBundleUrl`), enqueues the plan
 * (`v3.host.checks.enqueue`) and waits for the results (`v3.host.checks.await`). A check box
 * pulls: it claims the next job it can run (`checkPool.claim`), keeps the lease with a
 * heartbeat every {@link CHECK_HEARTBEAT_SECONDS} (`checkPool.heartbeat`), downloads the
 * source and the artifacts the job consumes and uploads the ones it produces
 * (`checkPool.artifactUrl`), and reports the result (`checkPool.complete`). A lease not
 * renewed for {@link CHECK_LEASE_SECONDS} is requeued, at most {@link CHECK_MAX_ATTEMPTS}
 * times. A claimed job carries the values of the secrets its spec names (`secretEnv`), read
 * by the api for the box's customer at claim time; no plan, spec, row or result holds a value.
 *
 * Instants on the wire are `Schema.DateTimeUtc` (ISO-8601 strings encoded). Optional fields
 * are absent, never null.
 *
 * @since 1.0.0
 */
import {
  V3_CHECK_OUTPUT_TAIL_CHARS,
  V3_TERMINAL_CHECK_JOB_STATES,
  V3CheckArtifact,
  V3CheckArtifactName,
  V3CheckEnv,
  V3CheckEnvName,
  V3CheckJobKey,
  V3CheckJobResult,
  V3CheckJobSpec,
  V3CheckJobState,
} from "@fm/db/models/V3Check";
import { sha256 } from "@noble/hashes/sha2";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils";
import { Array as Arr, Either, Option, ParseResult, Schema } from "effect";

export {
  V3_CHECK_OUTPUT_TAIL_CHARS as CHECK_OUTPUT_TAIL_CHARS,
  V3_TERMINAL_CHECK_JOB_STATES as TERMINAL_CHECK_JOB_STATES,
  V3CheckArtifact as CheckArtifact,
  V3CheckArtifactName as CheckArtifactName,
  V3CheckEnv as CheckEnv,
  V3CheckEnvName as CheckEnvName,
  V3CheckJobKey as CheckJobKey,
  V3CheckJobResult as CheckJobResult,
  V3CheckJobSpec as CheckJobSpec,
  V3CheckJobState as CheckJobState,
};

// ============================================================================
// Pool constants
// ============================================================================

/**
 * The runner pool of the check boxes. A box is an `orca_runners` row labelled
 * {@link CHECK_POOL_LABEL}; only such a runner may claim a job.
 *
 * @since 1.0.0
 */
export const CHECK_POOL = "checks";

/**
 * The runner label that places a box in the check pool.
 *
 * @since 1.0.0
 */
export const CHECK_POOL_LABEL = `pool=${CHECK_POOL}`;

/**
 * How long a claim or a heartbeat holds a job's lease.
 *
 * @since 1.0.0
 */
export const CHECK_LEASE_SECONDS = 60;

/**
 * How often a box renews the lease of the job it runs.
 *
 * @since 1.0.0
 */
export const CHECK_HEARTBEAT_SECONDS = 20;

/**
 * How many times a job is leased before an expired lease ends it as `error`.
 *
 * @since 1.0.0
 */
export const CHECK_MAX_ATTEMPTS = 3;

/**
 * The longest a long-poll (`checkPool.claim`, `v3.host.checks.await`) is held open.
 *
 * @since 1.0.0
 */
export const CHECK_LONG_POLL_MAX_SECONDS = 25;

/**
 * How long a presigned url stays valid.
 *
 * @since 1.0.0
 */
export const CHECK_PRESIGN_SECONDS = 900;

// ============================================================================
// Storage layout and the job container
// ============================================================================

/**
 * The archive bucket prefix every check-pool object lives under.
 *
 * @since 1.0.0
 */
export const CHECK_KEY_PREFIX = "v3-checks";

/**
 * The source bundle's format: `git archive --format=tar.gz <tree>` — the tree's files, no
 * `.git`. A box extracts it into an empty directory and commits it as one commit, so a job
 * can `git diff` what it changed.
 *
 * @since 1.0.0
 */
export const CHECK_SOURCE_FORMAT = "tar.gz";

/**
 * Where a job's checkout is mounted in its container (read-write, fresh per job).
 *
 * @since 1.0.0
 */
export const CHECK_JOB_WORKDIR = "/work";

/**
 * Where a job's artifacts are, in its container: each consumed artifact is the file
 * `<dir>/<name>` when the job starts, and each produced one must be `<dir>/<name>` when it
 * passes. The container sees it as env `CHECK_ARTIFACTS_DIR`.
 *
 * @since 1.0.0
 */
export const CHECK_JOB_ARTIFACTS_DIR = "/check-artifacts";

/**
 * A directory kept across jobs on a box (a package manager's cache), mounted in every job's
 * container. The container sees it as env `CHECK_CACHE_DIR`.
 *
 * @since 1.0.0
 */
export const CHECK_JOB_CACHE_DIR = "/check-cache";

/**
 * The env var names a job container sees for {@link CHECK_JOB_ARTIFACTS_DIR} and
 * {@link CHECK_JOB_CACHE_DIR}. The worker sets them; job scripts read them.
 *
 * @since 1.0.0
 */
export const CHECK_JOB_ENV = {
  artifactsDir: "CHECK_ARTIFACTS_DIR",
  cacheDir: "CHECK_CACHE_DIR",
} as const;

/**
 * Where a check box keeps the job cache that {@link CHECK_JOB_CACHE_DIR} maps to, owned by
 * the job user (uid {@link CHECK_JOB_UID}).
 *
 * @since 1.0.0
 */
export const CHECK_WORKER_CACHE_HOST_DIR = "/var/lib/fm-check-worker/cache";

/**
 * The uid a job container runs as. The worker makes the job's work and artifacts
 * directories writable by it.
 *
 * @since 1.0.0
 */
export const CHECK_JOB_UID = 1000;

/**
 * The check worker's compiled binary, its systemd unit and its environment file on a box.
 *
 * @since 1.0.0
 */
export const CHECK_WORKER_BINARY = "fm-check-worker";

/**
 * Where the check worker's release workflow publishes the binary in the release bucket:
 * `<prefix>/<version | stable>/<platform>`. The box bootstrap downloads the `stable` alias.
 *
 * @since 1.0.0
 */
export const CHECK_WORKER_RELEASE_PREFIX = "check-worker/releases";

/**
 * The platforms the check worker is released for.
 *
 * @since 1.0.0
 */
export const CHECK_WORKER_PLATFORMS = ["linux-amd64", "linux-arm64"] as const;

/**
 * The bucket key of a check-worker release for one platform.
 *
 * @since 1.0.0
 */
export const checkWorkerReleaseKey = (
  version: string,
  platform: (typeof CHECK_WORKER_PLATFORMS)[number],
): string => `${CHECK_WORKER_RELEASE_PREFIX}/${version}/${platform}`;

/**
 * @since 1.0.0
 */
export const CHECK_WORKER_SERVICE = "fm-check-worker.service";

/**
 * @since 1.0.0
 */
export const CHECK_WORKER_ENV_FILE = "/etc/fm-check-worker/env";

/**
 * The environment the check worker reads (written by the box's bootstrap into
 * {@link CHECK_WORKER_ENV_FILE}):
 *
 * - `apiUrl` — the orcaloops api the worker calls (`<url>/rpc`).
 * - `token` — the box's runner token.
 * - `memoryMb` — a job container's memory limit when its spec names none
 *   ({@link CHECK_WORKER_DEFAULT_MEMORY_MB}).
 * - `cpus` — a job container's cpus (default: every core of the box).
 * - `workDir` — where the worker keeps checkouts and the cache
 *   ({@link CHECK_WORKER_DEFAULT_WORK_DIR}).
 * - `docker` — the docker CLI (default `docker`).
 *
 * @since 1.0.0
 */
export const CHECK_WORKER_ENV = {
  apiUrl: "CHECK_WORKER_API_URL",
  token: "CHECK_WORKER_TOKEN",
  memoryMb: "CHECK_WORKER_MEMORY_MB",
  cpus: "CHECK_WORKER_CPUS",
  workDir: "CHECK_WORKER_WORK_DIR",
  docker: "CHECK_WORKER_DOCKER",
} as const;

/**
 * @since 1.0.0
 */
export const CHECK_WORKER_DEFAULT_MEMORY_MB = 6144;

/**
 * @since 1.0.0
 */
export const CHECK_WORKER_DEFAULT_WORK_DIR = "/var/lib/fm-check-worker";

/**
 * Where a commit's source bundle lives in the archive bucket: one object per tree, so
 * commits with the same content share it.
 *
 * @since 1.0.0
 */
export const checkSourceKey = (customerId: string, tree: string): string =>
  `${CHECK_KEY_PREFIX}/${customerId}/source/${tree}.${CHECK_SOURCE_FORMAT}`;

/**
 * Where an artifact a job produced lives in the archive bucket.
 *
 * @since 1.0.0
 */
export const checkArtifactKey = (customerId: string, jobId: string, name: string): string =>
  `${CHECK_KEY_PREFIX}/${customerId}/artifacts/${jobId}/${name}`;

// ============================================================================
// Backend switch
// ============================================================================

/**
 * Where the loop host runs its checks: `local` (today's parts, on its own runner) or `pool`
 * (enqueued to the check pool). Read by the host from `V3_CHECKS_BACKEND`.
 *
 * @since 1.0.0
 */
export const ChecksBackend = Schema.Literal("local", "pool");
export type ChecksBackend = typeof ChecksBackend.Type;

/**
 * @since 1.0.0
 */
export const DEFAULT_CHECKS_BACKEND: ChecksBackend = "local";

/**
 * @since 1.0.0
 */
export const isChecksBackend: (value: unknown) => value is ChecksBackend = Schema.is(ChecksBackend);

// ============================================================================
// The plan
// ============================================================================

/**
 * A commit's check jobs, as the repo's `checkPlan` command prints them (one JSON document on
 * stdout). {@link validateCheckPlan} checks what the schema cannot.
 *
 * @since 1.0.0
 */
export const CheckPlan = Schema.Struct({
  version: Schema.Literal(1),
  jobs: Schema.Array(V3CheckJobSpec),
}).annotations({ identifier: "CheckPlan" });
export type CheckPlan = typeof CheckPlan.Type;

/**
 * One problem with a plan.
 *
 * @since 1.0.0
 */
export interface CheckPlanProblem {
  /** Where, e.g. `jobs[2].needs[0]`. */
  readonly path: string;
  readonly message: string;
}

const duplicates = (values: ReadonlyArray<string>): ReadonlyArray<string> =>
  Arr.dedupe(values.filter((value, i) => values.indexOf(value) !== i));

const unknownNeeds = (plan: CheckPlan, known: ReadonlySet<string>): ReadonlyArray<CheckPlanProblem> =>
  plan.jobs.flatMap((job, i) =>
    job.needs.flatMap((need, j) =>
      known.has(need) ? [] : [{ path: `jobs[${i}].needs[${j}]`, message: `"${need}" is not a job in the plan` }]
    )
  );

const sharedArtifacts = (plan: CheckPlan): ReadonlyArray<CheckPlanProblem> => {
  const producers = new Map<string, ReadonlyArray<string>>();
  plan.jobs.forEach((job) =>
    Arr.dedupe(job.produces).forEach((name) => producers.set(name, [...(producers.get(name) ?? []), job.key]))
  );
  return [...producers.entries()].flatMap(([name, keys]) =>
    keys.length > 1
      ? [{ path: "jobs", message: `artifact "${name}" is produced by more than one job: ${keys.join(", ")}` }]
      : []
  );
};

const unproducedConsumes = (
  plan: CheckPlan,
  byKey: ReadonlyMap<string, V3CheckJobSpec>,
): ReadonlyArray<CheckPlanProblem> =>
  plan.jobs.flatMap((job, i) =>
    job.consumes.flatMap((name, j) =>
      job.needs.some((need) => byKey.get(need)?.produces.includes(name) === true)
        ? []
        : [{ path: `jobs[${i}].consumes[${j}]`, message: `"${name}" is not produced by a job "${job.key}" needs` }]
    )
  );

const cycles = (
  plan: CheckPlan,
  byKey: ReadonlyMap<string, V3CheckJobSpec>,
): ReadonlyArray<CheckPlanProblem> => {
  const state = new Map<string, "visiting" | "done">();
  const found: Array<ReadonlyArray<string>> = [];
  const visit = (job: V3CheckJobSpec, path: ReadonlyArray<string>): void => {
    const seen = state.get(job.key);
    if (seen === "done") return;
    if (seen === "visiting") {
      found.push([...path.slice(path.indexOf(job.key)), job.key]);
      return;
    }
    state.set(job.key, "visiting");
    job.needs.forEach((need) => {
      const next = byKey.get(need);
      if (next !== undefined) visit(next, [...path, job.key]);
    });
    state.set(job.key, "done");
  };
  plan.jobs.forEach((job) => visit(job, []));
  return found.map((cycle) => ({
    path: `jobs[${plan.jobs.findIndex((job) => job.key === cycle[0])}].needs`,
    message: `needs form a cycle: ${cycle.join(" → ")}`,
  }));
};

/**
 * Every problem with a plan, or the plan itself when there are none:
 * - it has at least one job;
 * - job keys are unique;
 * - every `needs` entry is a job in the plan;
 * - `needs` form no cycle;
 * - an artifact is produced by at most one job;
 * - every `consumes` entry is produced by one of the job's own `needs`.
 *
 * @since 1.0.0
 */
export const validateCheckPlan = (plan: CheckPlan): Either.Either<CheckPlan, ReadonlyArray<CheckPlanProblem>> => {
  const keys = plan.jobs.map((job) => job.key);
  const byKey = new Map(plan.jobs.map((job) => [job.key, job] as const));
  const problems: ReadonlyArray<CheckPlanProblem> = [
    ...(plan.jobs.length === 0 ? [{ path: "jobs", message: "a plan needs at least one job" }] : []),
    ...duplicates(keys).map((key) => ({ path: "jobs", message: `job key "${key}" appears more than once` })),
    ...unknownNeeds(plan, new Set(keys)),
    ...cycles(plan, byKey),
    ...sharedArtifacts(plan),
    ...unproducedConsumes(plan, byKey),
  ];
  return problems.length === 0 ? Either.right(plan) : Either.left(problems);
};

const pathOf = (path: ReadonlyArray<PropertyKey>): string =>
  path.reduce<string>(
    (acc, segment) =>
      typeof segment === "number" ? `${acc}[${segment}]` : acc === "" ? String(segment) : `${acc}.${String(segment)}`,
    "",
  );

const decodePlanJson = Schema.decodeUnknownEither(Schema.parseJson(CheckPlan), { errors: "all" });

/**
 * A plan from the `checkPlan` command's stdout: parsed, decoded and validated, every
 * problem at once. A problem with the document as a whole (not JSON) has the path `plan`.
 *
 * @since 1.0.0
 */
export const decodeCheckPlan = (text: string): Either.Either<CheckPlan, ReadonlyArray<CheckPlanProblem>> =>
  decodePlanJson(text).pipe(
    Either.mapLeft((error): ReadonlyArray<CheckPlanProblem> =>
      ParseResult.ArrayFormatter.formatErrorSync(error).map((issue) => {
        const path = pathOf(issue.path);
        return { path: path === "" ? "plan" : path, message: issue.message };
      })
    ),
    Either.flatMap(validateCheckPlan),
  );

// ============================================================================
// Results and the cache key
// ============================================================================

/**
 * The state a completed job ends in: `error` when the box or pool stopped it (its result
 * carries `environment`), else `passed` or `failed` by its verdict.
 *
 * @since 1.0.0
 */
export const checkJobStateOf = (result: V3CheckJobResult): "passed" | "failed" | "error" =>
  result.environment !== undefined ? "error" : result.pass ? "passed" : "failed";

/**
 * The digest an image reference is pinned to (`<repo>@sha256:<64 hex>`), or none when it is
 * not pinned. The pool runs jobs only in pinned images, so a cached result names exactly
 * the image it ran in.
 *
 * @since 1.0.0
 */
export const imageDigestOf = (image: string): Option.Option<string> =>
  Option.fromNullable(/@(sha256:[0-9a-f]{64})$/.exec(image)).pipe(Option.map((match) => match[1]!));

/**
 * What a result's reuse depends on beyond the job itself, as the host sends it with a plan:
 * the workspace repo, the checked commit's tree, and its merge base with the base branch.
 *
 * @since 1.0.0
 */
export const CheckCacheParts = Schema.Struct({
  repo: Schema.String,
  tree: Schema.String,
  mergeBase: Schema.String,
});
export type CheckCacheParts = typeof CheckCacheParts.Type;

/**
 * Everything a job's result depends on. Two jobs with equal inputs get one result: the
 * customer, the repo, the checked tree and its merge base, the job's key, its command and
 * env, and the digest of the image it runs in.
 *
 * @since 1.0.0
 */
export const CheckCacheKeyInput = Schema.Struct({
  ...CheckCacheParts.fields,
  customerId: Schema.String,
  jobKey: V3CheckJobKey,
  command: Schema.Array(Schema.String),
  env: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.String })),
  imageDigest: Schema.String,
});
export type CheckCacheKeyInput = typeof CheckCacheKeyInput.Type;

/**
 * A value {@link canonicalJson} can write.
 *
 * @since 1.0.0
 */
export type CanonicalValue =
  | string
  | number
  | boolean
  | null
  | ReadonlyArray<CanonicalValue>
  | { readonly [key: string]: CanonicalValue | undefined };

const isList = (value: CanonicalValue): value is ReadonlyArray<CanonicalValue> => Array.isArray(value);

/**
 * JSON with object keys sorted at every level and `undefined` members dropped, so equal
 * values always write the same text.
 *
 * @since 1.0.0
 */
export const canonicalJson = (value: CanonicalValue): string => {
  if (isList(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  const members = Object.keys(value).sort().flatMap((key) => {
    const member = value[key];
    return member === undefined ? [] : [`${JSON.stringify(key)}:${canonicalJson(member)}`];
  });
  return `{${members.join(",")}}`;
};

/**
 * The cache key's own version: changing what goes into the key bumps it, so no old key can
 * match a new one.
 *
 * @since 1.0.0
 */
export const CHECK_CACHE_KEY_VERSION = 1;

/**
 * A job result's cache key: the hex SHA-256 of the canonical JSON of its inputs. An absent
 * `env` and an empty one are the same key.
 *
 * @since 1.0.0
 */
export const checkCacheKey = (input: CheckCacheKeyInput): string =>
  bytesToHex(sha256(utf8ToBytes(canonicalJson({
    v: CHECK_CACHE_KEY_VERSION,
    customerId: input.customerId,
    repo: input.repo,
    tree: input.tree,
    mergeBase: input.mergeBase,
    jobKey: input.jobKey,
    command: input.command,
    env: input.env ?? {},
    imageDigest: input.imageDigest,
  }))));

// ============================================================================
// Errors on the wire
// ============================================================================

/**
 * The caller does not hold the job's lease at the attempt it named, so it must stop the job:
 * - `expired` — the lease ran out and the job was requeued (or ended);
 * - `superseded` — a later attempt of the job exists;
 * - `finished` — the job is already terminal.
 *
 * @since 1.0.0
 */
export class CheckLeaseLost extends Schema.TaggedError<CheckLeaseLost>()("CheckLeaseLost", {
  jobId: Schema.String,
  reason: Schema.Literal("expired", "superseded", "finished"),
  message: Schema.String,
}) {}

/**
 * A runner that is not a check box ({@link CHECK_POOL_LABEL}) asked for a job.
 *
 * @since 1.0.0
 */
export class CheckWorkerRefused extends Schema.TaggedError<CheckWorkerRefused>()("CheckWorkerRefused", {
  reason: Schema.Literal("not-in-pool"),
  message: Schema.String,
}) {}

/**
 * The archive bucket could not presign a url or answer whether an object exists.
 *
 * @since 1.0.0
 */
export class CheckStorageError extends Schema.TaggedError<CheckStorageError>()("CheckStorageError", {
  operation: Schema.Literal("presign", "exists"),
  message: Schema.String,
}) {}

// ============================================================================
// Worker rpcs: `checkPool.*`
// ============================================================================

const Attempt = Schema.Int.pipe(Schema.positive());
const WaitSeconds = Schema.Int.pipe(Schema.between(0, CHECK_LONG_POLL_MAX_SECONDS));

/**
 * A presigned url: GET to download, PUT to upload, valid until `expiresAt`.
 *
 * @since 1.0.0
 */
export const CheckPresignedUrl = Schema.Struct({
  url: Schema.String,
  method: Schema.Literal("GET", "PUT"),
  expiresAt: Schema.DateTimeUtc,
});
export type CheckPresignedUrl = typeof CheckPresignedUrl.Type;

/**
 * `checkPool.claim`: the next job this box can run. The api holds the call open up to
 * `waitSeconds` (default 0, at most {@link CHECK_LONG_POLL_MAX_SECONDS}) for one to appear,
 * and skips jobs whose `memoryMb` is above the box's.
 *
 * @since 1.0.0
 */
export const CheckClaimInput = Schema.Struct({
  waitSeconds: Schema.optional(WaitSeconds),
  memoryMb: Schema.optional(Schema.Int.pipe(Schema.positive())),
});
export type CheckClaimInput = typeof CheckClaimInput.Type;

/**
 * A claimed job. `attempt` is the lease's fencing token: heartbeat, complete and artifactUrl
 * must name it. `image` is pinned by digest ({@link imageDigestOf}).
 *
 * @since 1.0.0
 */
export const CheckClaimedJob = Schema.Struct({
  jobId: Schema.String,
  attempt: Attempt,
  spec: V3CheckJobSpec,
  image: Schema.String,
  leaseExpiresAt: Schema.DateTimeUtc,
  /** The values of `spec.secrets` the job's customer has, by name (a missing name is absent). Only
   *  when the job names a secret. Plain strings on the wire, over the box's authenticated rpc; never stored. */
  secretEnv: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Redacted(Schema.String) })),
});
export type CheckClaimedJob = typeof CheckClaimedJob.Type;

/**
 * `checkPool.claim`'s answer: a job, or none when nothing was ready in time.
 *
 * @since 1.0.0
 */
export const CheckClaimResult = Schema.Struct({ job: Schema.optional(CheckClaimedJob) });
export type CheckClaimResult = typeof CheckClaimResult.Type;

/**
 * The lease a call acts under.
 *
 * @since 1.0.0
 */
export const CheckLeaseRef = Schema.Struct({ jobId: Schema.String, attempt: Attempt });
export type CheckLeaseRef = typeof CheckLeaseRef.Type;

/**
 * `checkPool.heartbeat`: extend the lease by {@link CHECK_LEASE_SECONDS}.
 *
 * @since 1.0.0
 */
export const CheckHeartbeatResult = Schema.Struct({ leaseExpiresAt: Schema.DateTimeUtc });
export type CheckHeartbeatResult = typeof CheckHeartbeatResult.Type;

/**
 * `checkPool.complete`: the job's result. The api records it, ends the job in
 * {@link checkJobStateOf} its result, and answers with that state.
 *
 * @since 1.0.0
 */
export const CheckCompleteInput = Schema.Struct({ ...CheckLeaseRef.fields, result: V3CheckJobResult });
export type CheckCompleteInput = typeof CheckCompleteInput.Type;

/**
 * @since 1.0.0
 */
export const CheckCompleteResult = Schema.Struct({ state: V3CheckJobState });
export type CheckCompleteResult = typeof CheckCompleteResult.Type;

/**
 * What a box wants a url for:
 * - `source` — GET the job's source bundle;
 * - `consume` — GET an artifact the job consumes, from the need that produced it;
 * - `produce` — PUT an artifact the job produces.
 *
 * @since 1.0.0
 */
export const CheckArtifactTarget = Schema.Union(
  Schema.TaggedStruct("source", {}),
  Schema.TaggedStruct("consume", { name: V3CheckArtifactName }),
  Schema.TaggedStruct("produce", { name: V3CheckArtifactName }),
);
export type CheckArtifactTarget = typeof CheckArtifactTarget.Type;

/**
 * `checkPool.artifactUrl`: a presigned url for the job's source or one of its artifacts. A
 * name the job's spec does not list is refused.
 *
 * @since 1.0.0
 */
export const CheckArtifactUrlInput = Schema.Struct({ ...CheckLeaseRef.fields, target: CheckArtifactTarget });
export type CheckArtifactUrlInput = typeof CheckArtifactUrlInput.Type;

// ============================================================================
// Host rpcs: `v3.host.checks.*`
// ============================================================================

/**
 * `v3.host.checks.sourceBundleUrl`: where to upload a tree's source bundle. `ref` names it
 * in `enqueue`; `exists` says it is already uploaded (bundles are per tree), so the host
 * may skip the upload.
 *
 * @since 1.0.0
 */
export const CheckSourceUrlInput = Schema.Struct({ runId: Schema.String, tree: Schema.String });
export type CheckSourceUrlInput = typeof CheckSourceUrlInput.Type;

/**
 * @since 1.0.0
 */
export const CheckSourceUrl = Schema.Struct({
  ref: Schema.String,
  exists: Schema.Boolean,
  upload: CheckPresignedUrl,
});
export type CheckSourceUrl = typeof CheckSourceUrl.Type;

/**
 * `v3.host.checks.enqueue`: queue a commit's plan.
 *
 * - `plan` is validated ({@link validateCheckPlan}) before anything is written.
 * - `image` is the pinned image every job runs in.
 * - `source` is the `ref` from `sourceBundleUrl` for `cache.tree`.
 * - `cache` with the caller's customer, the image's digest and each job's key, command and
 *   env make each job's {@link checkCacheKey}.
 * - `sha` and `by` (`orchestrator`, `subagent:a3`) are recorded so a later reuse can say
 *   whose run it was.
 *
 * @since 1.0.0
 */
export const CheckEnqueueInput = Schema.Struct({
  runId: Schema.String,
  plan: CheckPlan,
  image: Schema.String,
  source: Schema.String,
  cache: CheckCacheParts,
  sha: Schema.String,
  by: Schema.String,
});
export type CheckEnqueueInput = typeof CheckEnqueueInput.Type;

/**
 * The earlier job a result was reused from.
 *
 * @since 1.0.0
 */
export const CheckReuse = Schema.Struct({
  jobId: Schema.String,
  runId: Schema.String,
  sha: Schema.String,
  by: Schema.String,
});
export type CheckReuse = typeof CheckReuse.Type;

/**
 * One job as the host sees it. `result` is present once the job is terminal; `reusedFrom`
 * when its result came from the cache (the job never ran; `result.ms` is the earlier run's
 * time).
 *
 * @since 1.0.0
 */
export const CheckJobStatus = Schema.Struct({
  jobId: Schema.String,
  key: V3CheckJobKey,
  state: V3CheckJobState,
  attempt: Schema.Int.pipe(Schema.nonNegative()),
  result: Schema.optional(V3CheckJobResult),
  reusedFrom: Schema.optional(CheckReuse),
});
export type CheckJobStatus = typeof CheckJobStatus.Type;

/**
 * `v3.host.checks.enqueue`'s answer: every job of the plan, in plan order, with its id —
 * already terminal when its result was reused.
 *
 * @since 1.0.0
 */
export const CheckEnqueueResult = Schema.Struct({ jobs: Schema.Array(CheckJobStatus) });
export type CheckEnqueueResult = typeof CheckEnqueueResult.Type;

/**
 * `v3.host.checks.await`: a long-poll. The api answers with the status of every job named
 * once one or more of them is terminal, or after `waitSeconds` (default and most
 * {@link CHECK_LONG_POLL_MAX_SECONDS}) when none is. The host calls again with the jobs
 * still open.
 *
 * @since 1.0.0
 */
export const CheckAwaitInput = Schema.Struct({
  runId: Schema.String,
  jobIds: Schema.NonEmptyArray(Schema.String),
  waitSeconds: Schema.optional(WaitSeconds),
});
export type CheckAwaitInput = typeof CheckAwaitInput.Type;

/**
 * @since 1.0.0
 */
export const CheckAwaitResult = Schema.Struct({ jobs: Schema.Array(CheckJobStatus) });
export type CheckAwaitResult = typeof CheckAwaitResult.Type;
